import { AnimatePresence, motion } from "framer-motion"
import { useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"
import type { ToastInfo } from "~/context/ToastContext"
import Toast from "~/modules/toasts/Toast"

const Layout = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 500px;

  z-index: 9000;
`

const toastPadding = 16

export interface ToastContainerProps {
  toasts: ToastInfo[]
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts }) => {
  const containerRef = useRef(null)
  const [heightOffsets, setHeightOffsets] = useState<{
    heights: number[]
    offsets: number[]
  }>({
    heights: [],
    offsets: [],
  })

  const measureToastHeight = (toast: ToastInfo) => {
    const toastElement = document.getElementById(toast.id)

    if (toastElement) {
      const height = toastElement.getBoundingClientRect().height
      return height
    }
    return 0
  }

  useLayoutEffect(() => {
    const heights = toasts.map(measureToastHeight)
    const offsets = heights.reduce<number[]>((acc, _next, index) => {
      if (index === 0) {
        return [0]
      }

      const offset = acc[index - 1] + heights[index - 1] + toastPadding
      return [...acc, offset]
    }, [])

    setHeightOffsets({ heights, offsets })
  }, [toasts])

  const { heights, offsets } = heightOffsets

  return (
    <Layout ref={containerRef}>
      <AnimatePresence initial={false}>
        {toasts.map((toast, index) => {
          const offsetY = offsets[index] ?? 0
          const height = heights[index] ?? 0

          return (
            <motion.div
              key={toast.id}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
              }}
              initial={{
                opacity: 0,
                transform: `translateY(${height}px)`,
              }}
              animate={{
                opacity: 1,
                transform: `translateY(-${offsetY}px)`,
              }}
              exit={{
                opacity: 0,
                transform: `translateY(-${offsetY - 2 * height}px)`,
              }}
            >
              <Toast id={toast.id} type={toast.type} message={toast.message} />
            </motion.div>
          )
        })}
      </AnimatePresence>
    </Layout>
  )
}

export default ToastContainer
